import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideoFileOutlinedIcon from "@mui/icons-material/VideoFileOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AdminServices } from "../../../../../services/admin/AdminServices";
import { Spinner } from "../../../../../components/Spinner/Spinner.component";
import {
  ScreenInfoApiResponse,
  StoreInfoApiResponse,
} from "../../PublicScreens.types";
import ScreenDetails from "./components/ScreenDetails";
import StoreDetails from "./components/StoreDetails";
import ImageSlider from "../../../../../components/ImageSlider";
import toast from "react-hot-toast";

const minutesToTime = (mins: number) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const ScreenInfo = () => {
  const { screenId } = useParams<{ screenId: string }>();
  const navigate = useNavigate();

  const [screenInfo, setScreenInfo] = useState<ScreenInfoApiResponse | null>(
    null,
  );
  const [storeInfo, setStoreInfo] = useState<StoreInfoApiResponse | null>(null);

  const [screenMode, setScreenMode] = useState<"view" | "edit">("view");
  const [storeMode, setStoreMode] = useState<"view" | "edit">("view");

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!screenId) return;

    const fetchData = async () => {
      try {
        const screenRes = await AdminServices.getScreenInfoByScreenId(screenId);
        setScreenInfo(screenRes.response);

        if (screenRes?.response?.storeId) {
          const storeRes = await AdminServices.getStoreInfoByStoreId(
            screenRes.response.storeId,
          );
          setStoreInfo(storeRes.response);
        }
      } catch (err) {
        console.error("Failed to fetch screen/store info", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [screenId]);

  if (loading) return <Spinner />;
  if (!screenInfo) return <div className="p-6">Screen not found</div>;

  const TYPE = {
    TV: "Screen",
    CHANNEL: "Channel",
  };

  const handleMediaUpload = async (file: File, type: "IMAGE" | "VIDEO") => {
    try {
      const res = await AdminServices.uploadMediaFile(
        file,
        screenInfo.orgId,
        type,
        `screen-${type.toLowerCase()}-${screenId}`,
      );

      const mediaUrl = res?.response?.mediaUrl;
      if (!mediaUrl) return;

      setScreenInfo((prev) =>
        prev
          ? {
              ...prev,
              imageUrl: type === "IMAGE" ? mediaUrl : prev.imageUrl,
              videoUrl: type === "VIDEO" ? mediaUrl : prev.videoUrl,
            }
          : prev,
      );
    } catch (err) {
      console.error(`${type} upload failed`, err);
    }
  };

  const handleUpdateScreenInfo = async () => {
    if (!screenInfo || !screenId) return;

    try {
      const payload = {
        id: Number(screenId),
        name: screenInfo.name,
        type: screenInfo.type,
        status: screenInfo.status,
        resolution: screenInfo.resolution,
        orientation: screenInfo.orientation,
        merchantAdsQuota: screenInfo.merchantAdsQuota,
        isPublic: screenInfo.isPublic,
        size: screenInfo.size,
        screenCategory: screenInfo.screenCategory,

        ...(screenInfo.imageUrl && { imageUrl: screenInfo.imageUrl }),
        ...(screenInfo.videoUrl && { videoUrl: screenInfo.videoUrl }),
      };

      const res = await AdminServices.updateScreenInfo(payload);

      // optional: API may return updated object
      // if (res?.response) {
      //   setScreenInfo(res.response);
      // }
      toast.success(`Screen details updated successfully (ID: ${screenId})`);
      navigate(-1);
      setScreenMode("view");
    } catch (err) {
      console.error("Failed to update screen info", err);
      toast.error("Failed to update screen info")
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-10 border-b bg-white">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="rounded-lg border p-2 hover:bg-gray-50"
          >
            <ArrowBackIcon />
          </button>

          <div>
            <h2 className="text-2xl font-bold">Screen Info</h2>
            <p className="text-xs text-gray-500">{screenId}</p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_3fr] gap-6 items-start">
          {/* ================= LEFT COLUMN — MEDIA ================= */}
          <div className="rounded-xl border bg-white p-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-500">
              Screen Media
            </h3>

            {/* Media Preview */}
            <div className=" rounded-lg bg-gray-100 overflow-hidden h-[25vh] w-[25vw]">
              {screenInfo.imageUrl || screenInfo.videoUrl ? (
                <ImageSlider
                  slidesPerView={1}
                  spaceBetween={12}
                  // showArrowsInside ={true}
                  // parentClass="h-full"
                  // className="h-full"
                >
                  {screenInfo.imageUrl && (
                    <img
                      src={screenInfo.imageUrl}
                      alt="Screen Image"
                      className="h-full w-full object-cover rounded-lg"
                    />
                  )}

                  {screenInfo.videoUrl && (
                    <video
                      src={screenInfo.videoUrl}
                      controls
                      className="h-full w-full object-cover rounded-lg"
                    />
                  )}
                </ImageSlider>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <span className="text-sm text-gray-400">
                    No media available
                  </span>
                </div>
              )}
            </div>

            {/* Upload Actions */}
            {screenMode === "edit" && (
              <div className="mt-4 flex gap-3">
                {/* Upload Image */}
                <label className="flex-1 cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleMediaUpload(file, "IMAGE");
                    }}
                  />
                  <div className="flex items-center justify-center gap-2 rounded-lg border  py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 transition">
                    <ImageOutlinedIcon fontSize="small" />
                    Upload Image
                  </div>
                </label>

                {/* Upload Video */}
                <label className="flex-1 cursor-pointer">
                  <input
                    type="file"
                    accept="video/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleMediaUpload(file, "VIDEO");
                    }}
                  />
                  <div className="flex items-center justify-center gap-2 rounded-lg border  py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 transition">
                    <VideoFileOutlinedIcon fontSize="small" />
                    Upload Video
                  </div>
                </label>
              </div>
            )}

            {/* Info */}
            <div className="mt-4 space-y-2">
              
              <InfoBox
                label="Store Name"
                value={storeInfo?.storeName ?? "-"}
              />
              <InfoBox
                label="Visibility"
                value={screenInfo.isPublic ? "Public" : "Private"}
              />
              <InfoBox label="Status" value={screenInfo.status} />
            </div>
          </div>

          {/* ================= RIGHT COLUMN — STACKED ================= */}
          <div className="flex flex-col gap-6">
            {/* ================= SCREEN INFO ================= */}
            {screenInfo && (
              <ScreenDetails
                screenInfo={screenInfo}
                screenMode={screenMode}
                setScreenMode={setScreenMode}
                setScreenInfo={setScreenInfo}
                onSave = {handleUpdateScreenInfo}
              />
            )}

            {/* ================= STORE INFO ================= */}
            {storeInfo ? (
              <StoreDetails
                storeInfo={storeInfo}
                storeMode={storeMode}
                setStoreMode={setStoreMode}
                setStoreInfo={setStoreInfo}
                minutesToTime={minutesToTime}
              />
            ) : (
              <p className="text-sm text-gray-400">No store info available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreenInfo;

const InfoBox = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-lg border p-3">
    <p className="text-xs text-gray-500 font-semibold">{label}</p>
    <p className="text-sm font-bold">{value}</p>
  </div>
);
